import { Zap, BookOpen, SquareCheck, Circle, Bug, Folder, icons } from 'lucide-react';

/**
 * 작업 유형별 아이콘 및 스타일 설정 (공통).
 * @deprecated CHG-052: 동적 이슈 타입에서는 issueTypeDef의 icon/color 사용 권장.
 *   하위 호환 및 issueTypeDef가 없는 경우 폴백으로 사용.
 */
export const ISSUE_TYPE_CONFIG: Record<
  string,
  { Icon: typeof Zap; color: string; bg: string; text: string; label: string }
> = {
  EPIC: { Icon: Zap, color: 'text-purple-600', bg: 'bg-purple-100', text: 'text-purple-700', label: '에픽' },
  Epic: { Icon: Zap, color: 'text-purple-600', bg: 'bg-purple-100', text: 'text-purple-700', label: '에픽' },
  STORY: { Icon: BookOpen, color: 'text-green-600', bg: 'bg-green-100', text: 'text-green-700', label: '스토리' },
  Story: { Icon: BookOpen, color: 'text-green-600', bg: 'bg-green-100', text: 'text-green-700', label: '스토리' },
  TASK: { Icon: SquareCheck, color: 'text-blue-600', bg: 'bg-blue-100', text: 'text-blue-700', label: '작업' },
  Task: { Icon: SquareCheck, color: 'text-blue-600', bg: 'bg-blue-100', text: 'text-blue-700', label: '작업' },
  SUB_TASK: { Icon: Circle, color: 'text-gray-500', bg: 'bg-gray-100', text: 'text-gray-700', label: '하위작업' },
  Subtask: { Icon: Circle, color: 'text-gray-500', bg: 'bg-gray-100', text: 'text-gray-700', label: '하위작업' },
  BUG: { Icon: Bug, color: 'text-red-600', bg: 'bg-red-100', text: 'text-red-700', label: '버그' },
  Bug: { Icon: Bug, color: 'text-red-600', bg: 'bg-red-100', text: 'text-red-700', label: '버그' },
  GROUP: { Icon: Folder, color: 'text-amber-600', bg: 'bg-amber-100', text: 'text-amber-700', label: '그룹' },
};

/** 기본 fallback (TASK) */
export const DEFAULT_ISSUE_TYPE = ISSUE_TYPE_CONFIG.TASK;

// ── CHG-052: 동적 이슈 타입 지원 ──

/** API에서 반환되는 이슈 타입 정의 */
export interface IssueTypeDef {
  issueTypeId: string;
  name: string;
  icon: string;          // lucide icon name (kebab-case)
  color: string;         // HEX color
  customIconPath?: string | null;  // CHG-053: 커스텀 아이콘 경로
}

/** kebab-case → PascalCase */
function toPascalCase(s: string): string {
  return s.split('-').map((p) => p.charAt(0).toUpperCase() + p.slice(1)).join('');
}

/** HEX 색상 → Tailwind-like CSS class 생성 (인라인 style용) */
export function hexToStyle(hex: string) {
  return { color: hex };
}

// ── CHG-052: 글로벌 이슈 타입 레지스트리 ──
// projectIssueTypes를 등록하면 이름만으로도 동적 아이콘/색상 조회 가능
let _registeredTypes = new Map<string, IssueTypeDef>();

/**
 * 프로젝트 이슈 타입을 글로벌 레지스트리에 등록한다.
 * useProjectIssueTypes 훅에서 데이터 로드 후 호출.
 */
export function registerProjectIssueTypes(types: IssueTypeDef[]) {
  const map = new Map<string, IssueTypeDef>();
  for (const t of types) {
    map.set(t.name, t);
    map.set(t.name.toUpperCase(), t);          // EPIC, STORY 등
    map.set(t.name.replace(/\s/g, '_').toUpperCase(), t); // SUB_TASK 등
  }
  _registeredTypes = map;
}

/** 레지스트리에서 이름으로 IssueTypeDef 조회 */
function lookupRegisteredType(name: string): IssueTypeDef | undefined {
  return _registeredTypes.get(name) ?? _registeredTypes.get(name.toUpperCase());
}

/** 아이콘 이름으로 lucide 컴포넌트 조회 */
function resolveIcon(iconName: string): typeof Zap | null {
  const pascalName = toPascalCase(iconName);
  const IconComp = icons[pascalName as keyof typeof icons];
  return IconComp ? (IconComp as typeof Zap) : null;
}

/**
 * issueTypeDef에서 아이콘 컴포넌트를 동적으로 가져온다.
 * 우선순위: issueTypeDef → 레지스트리(이름) → ISSUE_TYPE_CONFIG 폴백
 */
export function resolveIssueTypeIcon(
  issueTypeDef?: IssueTypeDef | null,
  issueType?: string,
): { Icon: typeof Zap; hexColor?: string } {
  // 1. issueTypeDef 직접 전달
  if (issueTypeDef?.icon) {
    const Icon = resolveIcon(issueTypeDef.icon);
    if (Icon) return { Icon, hexColor: issueTypeDef.color };
  }

  // 2. 레지스트리에서 이름으로 조회
  const key = issueType ?? issueTypeDef?.name ?? '';
  const registered = lookupRegisteredType(key);
  if (registered) {
    const Icon = resolveIcon(registered.icon);
    if (Icon) return { Icon, hexColor: registered.color };
  }

  // 3. ISSUE_TYPE_CONFIG 폴백
  const config = ISSUE_TYPE_CONFIG[key] ?? DEFAULT_ISSUE_TYPE;
  return { Icon: config.Icon };
}

/**
 * issueTypeDef 또는 issueType 문자열에서 전체 설정을 가져온다.
 * 우선순위: issueTypeDef → 레지스트리(이름) → ISSUE_TYPE_CONFIG 폴백
 */
export function resolveIssueTypeConfig(
  issueTypeDef?: IssueTypeDef | null,
  issueType?: string,
) {
  const key = issueType ?? issueTypeDef?.name ?? 'TASK';
  const fallback = ISSUE_TYPE_CONFIG[key] ?? DEFAULT_ISSUE_TYPE;

  // 1. issueTypeDef 직접 전달
  if (issueTypeDef?.icon) {
    const Icon = resolveIcon(issueTypeDef.icon);
    if (Icon) {
      return {
        Icon,
        color: '',
        hexColor: issueTypeDef.color,
        customIconPath: issueTypeDef.customIconPath ?? undefined,
        bg: fallback.bg,
        text: fallback.text,
        label: issueTypeDef.name,
      };
    }
  }

  // 2. 레지스트리에서 이름으로 조회
  const registered = lookupRegisteredType(key);
  if (registered) {
    const Icon = resolveIcon(registered.icon);
    if (Icon) {
      return {
        Icon,
        color: '',
        hexColor: registered.color,
        customIconPath: registered.customIconPath ?? undefined,
        bg: fallback.bg,
        text: fallback.text,
        label: registered.name,
      };
    }
  }

  // 3. ISSUE_TYPE_CONFIG 폴백
  return { ...fallback, hexColor: undefined, customIconPath: undefined };
}
